import { messagesDe } from "./messages.de";
import type { MessageKey } from "./messages.de";
import { messagesEn } from "./messages.en";

/** Supported languages; German is the default (spec 11.2). */
export type Locale = "de" | "en";

/** Supported languages in display order. */
export const supportedLocales: readonly Locale[] = ["de", "en"];

/** Values inserted into `{name}` placeholders. */
export type MessageParameters = Readonly<Record<string, string | number>>;

/**
 * Base key of a message with plural forms: the dictionaries hold `<base>.one` and `<base>.other`
 * (and may hold further categories of `Intl.PluralRules`), and the translation picks the form by
 * the parameter `count` (UX-114).
 */
export type PluralKey = MessageKey extends infer Key
  ? Key extends `${infer Base}.other`
    ? Base
    : never
  : never;

/** Key accepted by the translation function: a message key or the base key of plural forms. */
export type TranslationKey = MessageKey | PluralKey;

/** Translates a message key. */
export type Translate = (key: TranslationKey, parameters?: MessageParameters) => string;

const dictionaries: Readonly<Record<Locale, Readonly<Record<MessageKey, string>>>> = {
  de: messagesDe,
  en: messagesEn,
};

/**
 * Checks whether a value is a supported locale.
 *
 * @param value - The value to check.
 * @returns `true` for a supported locale.
 */
export function isLocale(value: unknown): value is Locale {
  const supported = value === "de" || value === "en";

  return supported;
}

/**
 * Returns the BCP 47 tag used for `Intl` formatters of a locale (dates as `24.09.2026` in German,
 * `24/09/2026` in English).
 *
 * @param locale - The locale.
 * @returns The language tag.
 */
export function languageTagFor(locale: Locale): string {
  const tag = locale === "de" ? "de-DE" : "en-GB";

  return tag;
}

/**
 * Checks whether a text is a known message key (for keys built from server values).
 *
 * @param value - The candidate key.
 * @returns `true` if the key exists.
 */
export function isMessageKey(value: string): value is MessageKey {
  const known = Object.hasOwn(messagesDe, value);

  return known;
}

/**
 * Replaces `{name}` placeholders; unknown placeholders stay visible to reveal missing values.
 * Numbers are formatted with the given number format (`1.234,5` in German), otherwise as is.
 *
 * @param template - The message template.
 * @param parameters - The values.
 * @param numberFormat - Format for numeric values; omitted, numbers are inserted unformatted.
 * @returns The formatted message.
 */
export function formatMessage(
  template: string,
  parameters: MessageParameters = {},
  numberFormat?: Intl.NumberFormat,
): string {
  const formatted = template.replace(/\{(\w+)\}/gu, (placeholder, name: string) => {
    const value = parameters[name];
    let replacement: string;

    if (value === undefined) {
      replacement = placeholder;
    } else if (typeof value === "number" && numberFormat !== undefined) {
      replacement = numberFormat.format(value);
    } else {
      replacement = String(value);
    }

    return replacement;
  });

  return formatted;
}

/**
 * Determines the plural category of a count; a count given as numeric text is accepted, anything
 * else falls back to `other`.
 *
 * @param count - The value of the parameter `count`.
 * @param pluralRules - Plural rules of the language.
 * @returns The plural category.
 */
function pluralCategory(
  count: string | number | undefined,
  pluralRules: Intl.PluralRules,
): Intl.LDMLPluralRule {
  const value = typeof count === "string" && count.trim() !== "" ? Number(count) : count;
  const category =
    typeof value === "number" && Number.isFinite(value) ? pluralRules.select(value) : "other";

  return category;
}

/**
 * Finds the template of a key: the message itself, or for a plural key the form of the category
 * of `count`, falling back to `other`; an unknown key is returned as is, so it stays visible.
 *
 * @param dictionary - Messages of the language.
 * @param key - The key.
 * @param parameters - The values, including `count` for plural keys.
 * @param pluralRules - Plural rules of the language.
 * @returns The template.
 */
function templateFor(
  dictionary: Readonly<Record<string, string>>,
  key: string,
  parameters: MessageParameters,
  pluralRules: Intl.PluralRules,
): string {
  const direct = dictionary[key];

  if (direct !== undefined) {
    return direct;
  }

  const category = pluralCategory(parameters["count"], pluralRules);
  const template = dictionary[`${key}.${category}`] ?? dictionary[`${key}.other`] ?? key;

  return template;
}

/**
 * Creates the translation function for a locale. Plural keys pick their form with
 * `Intl.PluralRules`, numeric parameters are formatted with `Intl.NumberFormat` of the locale.
 *
 * @param locale - The locale.
 * @returns The translation function.
 */
export function createTranslator(locale: Locale): Translate {
  const dictionary: Readonly<Record<string, string>> = dictionaries[locale];
  const languageTag = languageTagFor(locale);
  const pluralRules = new Intl.PluralRules(languageTag);
  const numberFormat = new Intl.NumberFormat(languageTag);
  const translate: Translate = (key, parameters = {}) => {
    const template = templateFor(dictionary, key, parameters, pluralRules);
    const message = formatMessage(template, parameters, numberFormat);

    return message;
  };

  return translate;
}
